'use client';

import { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, Clock, Calendar, ArrowRight, Sparkles, BookOpen, ShieldCheck, Tag, X } from 'lucide-react';
import Breadcrumbs from '@/components/Breadcrumbs';
import type { BlogPost } from '@/lib/editorial';

function formatDate(value: string) {
  try {
    return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(value));
  } catch {
    return value;
  }
}

function getReadTime(content: string) {
  if (!content) return '3 min read';
  const words = content.replace(/[#*`_~]/g, '').trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

function getAuthorInitials(name: string) {
  if (!name) return 'AJ';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

type BlogIndexClientProps = {
  posts: BlogPost[];
};

export default function BlogIndexClient({ posts }: BlogIndexClientProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState<string>('All');

  // Extract unique categories/tags from all posts
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    posts.forEach((post) => {
      post.related_product_tags?.forEach((t) => {
        if (t && t.trim()) tagSet.add(t.trim());
      });
    });
    return ['All', ...Array.from(tagSet)];
  }, [posts]);

  // Featured post is either the one marked is_featured or the first post
  const featuredPost = useMemo(() => {
    return posts.find((p) => p.is_featured) || posts[0];
  }, [posts]);

  // Remaining posts filtered by search query and category tag
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesSearch =
        searchQuery.trim() === '' ||
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.related_product_tags.some((t) => t.toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesTag =
        selectedTag === 'All' ||
        post.related_product_tags.some((t) => t.toLowerCase() === selectedTag.toLowerCase());

      return matchesSearch && matchesTag;
    });
  }, [posts, searchQuery, selectedTag]);

  // Grid posts (if no active filter, omit featured post from grid to avoid repetition)
  const gridPosts = useMemo(() => {
    if (searchQuery || selectedTag !== 'All' || !featuredPost) {
      return filteredPosts;
    }
    return filteredPosts.filter((p) => p.id !== featuredPost.id);
  }, [filteredPosts, featuredPost, searchQuery, selectedTag]);

  return (
    <div className="mx-auto w-full max-w-[1720px] px-4 sm:px-6 lg:px-8 2xl:px-10">
      <Breadcrumbs items={[{ label: 'Blog' }]} className="justify-center mb-2" />
      {/* Editorial Page Header */}
      <header className="py-4 sm:py-6 text-center max-w-3xl mx-auto">
        <h1 className="mt-2 font-serif text-2xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-accent leading-tight">
          Authentic Guides to Sacred Rudraksha, Gemstones & Astrology
        </h1>

        <p className="mt-2 sm:mt-3 text-sm sm:text-base leading-relaxed text-text-secondary max-w-2xl mx-auto">
          Learn how to select genuine lab-certified Rudrakshas, choose the right gemstones for your Kundali, and perform authentic daily rituals with complete confidence.
        </p>
      </header>

      {/* Featured Guide Banner (Shown when no search/tag filter is active) */}
      {featuredPost && !searchQuery && selectedTag === 'All' && (
        <section aria-label="Featured Guide" className="mb-6 sm:mb-10">
          <article className="group relative overflow-hidden rounded-3xl border border-orange-100/90 bg-white shadow-md shadow-orange-950/5 transition-all duration-300 hover:shadow-xl hover:border-orange-200">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Image Side (7 cols on laptop) */}
              <div className="lg:col-span-7 relative min-h-[220px] sm:min-h-[320px] lg:min-h-[380px] overflow-hidden bg-gradient-to-br from-amber-100/60 via-orange-100/40 to-white">
                {featuredPost.hero_image_url ? (
                  <img
                    src={featuredPost.hero_image_url}
                    alt={featuredPost.hero_image_alt || featuredPost.title}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center bg-gradient-to-br from-orange-100 via-amber-50 to-orange-50">
                    <BookOpen className="h-16 w-16 text-primary/40 mb-3" />
                    <span className="text-sm font-semibold tracking-wider uppercase text-primary">Astroji Featured Guide</span>
                  </div>
                )}
                {/* Overlay Badge */}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-primary px-3 py-1 text-xs font-semibold text-white shadow-md">
                    Featured Guide
                  </span>
                  {featuredPost.related_product_tags?.[0] && (
                    <span className="rounded-full bg-accent/90 backdrop-blur-md px-3 py-1 text-xs font-medium text-white shadow-xs">
                      {featuredPost.related_product_tags[0]}
                    </span>
                  )}
                </div>
              </div>

              {/* Content Side (5 cols on laptop) */}
              <div className="lg:col-span-5 flex flex-col justify-between p-5 sm:p-8 lg:p-10 bg-gradient-to-b from-white via-orange-50/20 to-white">
                <div>
                  <div className="flex items-center gap-3 text-xs text-text-secondary font-medium mb-2.5">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5 text-primary" />
                      {formatDate(featuredPost.published_at)}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5 text-primary" />
                      {getReadTime(featuredPost.content_markdown)}
                    </span>
                  </div>

                  <h2 className="font-serif text-xl sm:text-3xl font-bold text-accent leading-tight group-hover:text-primary transition-colors">
                    <Link href={`/blog/${featuredPost.slug}`}>{featuredPost.title}</Link>
                  </h2>

                  <p className="mt-2.5 sm:mt-4 text-xs sm:text-base leading-relaxed text-text-secondary line-clamp-3 sm:line-clamp-4">
                    {featuredPost.excerpt}
                  </p>
                </div>

                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-orange-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-gradient-to-tr from-accent to-primary text-xs font-bold text-white shadow-xs">
                      {getAuthorInitials(featuredPost.author_name)}
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm font-semibold text-accent leading-tight">{featuredPost.author_name}</p>
                      {featuredPost.author_role && (
                        <p className="text-[11px] sm:text-xs text-text-secondary">{featuredPost.author_role}</p>
                      )}
                    </div>
                  </div>

                  <Link
                    href={`/blog/${featuredPost.slug}`}
                    className="inline-flex items-center gap-1.5 sm:gap-2 rounded-xl bg-accent px-3.5 py-2 sm:px-4 sm:py-2.5 text-xs sm:text-sm font-semibold text-white shadow-md transition-all hover:bg-primary hover:shadow-lg active:scale-95 shrink-0"
                  >
                    <span>Read Guide</span>
                    <ArrowRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </section>
      )}

      {/* Filter & Search Toolbar */}
      <section className="relative md:sticky md:top-16 z-20 mb-6 sm:mb-8 rounded-2xl border border-orange-100/90 bg-white/95 p-2.5 sm:p-4 shadow-xs backdrop-blur-md">
        <div className="flex flex-col md:flex-row gap-2.5 sm:gap-4 items-center justify-between">
          {/* Category Tag Pills */}
          <div className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
            {allTags.map((tag) => {
              const isSelected = selectedTag.toLowerCase() === tag.toLowerCase();
              return (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`inline-flex items-center gap-1 sm:gap-1.5 rounded-full px-3 py-1.5 sm:px-4 sm:py-2 text-[11px] sm:text-xs font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? 'bg-primary text-accent-dark shadow-xs font-bold'
                      : 'bg-orange-50/70 text-accent hover:bg-orange-100/70'
                  }`}
                >
                  {tag === 'All' ? <BookOpen className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <Tag className="h-3 w-3" />}
                  <span>{tag}</span>
                </button>
              );
            })}
          </div>

          {/* Search Box */}
          <div className="relative w-full md:w-72 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-text-secondary pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search guides..."
              className="w-full rounded-full border border-orange-200 bg-orange-50/30 pl-9 pr-8 py-1.5 sm:py-2 text-xs sm:text-sm text-accent placeholder:text-text-secondary/60 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-secondary hover:text-accent p-1"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Grid Section Header */}
      <div className="mb-4 sm:mb-6 flex items-center justify-between">
        <h2 className="font-serif text-lg sm:text-2xl font-bold text-accent">
          {selectedTag !== 'All' ? `${selectedTag} Guides` : searchQuery ? 'Search Results' : 'Latest Guides'}
        </h2>
        <span className="text-[11px] sm:text-xs font-medium text-text-secondary bg-orange-50 px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full border border-orange-100">
          {gridPosts.length} {gridPosts.length === 1 ? 'guide' : 'guides'}
        </span>
      </div>

      {/* 4-Column Responsive Grid on Laptop/Desktop */}
      {gridPosts.length > 0 ? (
        <section aria-label="Guides Grid" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6 mb-12 sm:mb-16">
          {gridPosts.map((post) => (
            <article
              key={post.id}
              className="group flex flex-col justify-between rounded-2xl border border-orange-100/80 bg-white p-4 sm:p-5 shadow-xs transition-all duration-300 hover:-translate-y-1.5 hover:border-orange-200 hover:shadow-xl relative overflow-hidden"
            >
              <div>
                {/* Card Image */}
                <Link href={`/blog/${post.slug}`} className="block relative aspect-[16/10] overflow-hidden rounded-xl bg-gradient-to-br from-amber-50 to-orange-100/50 mb-3 sm:mb-4 border border-orange-100/50">
                  {post.hero_image_url ? (
                    <img
                      src={post.hero_image_url}
                      alt={post.hero_image_alt || `${post.title} - AstroJi Spiritual Guide`}
                      className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                      <BookOpen className="h-9 w-9 text-primary/40 mb-1" />
                      <span className="text-xs font-medium text-text-secondary">Astroji Guide</span>
                    </div>
                  )}

                  {post.related_product_tags?.[0] && (
                    <span className="absolute top-2.5 left-2.5 rounded-full bg-accent/85 backdrop-blur-md px-2.5 py-0.5 text-[10px] sm:text-[11px] font-semibold text-white shadow-xs">
                      {post.related_product_tags[0]}
                    </span>
                  )}
                </Link>

                {/* Metadata Row */}
                <div className="flex items-center gap-2.5 text-[11px] sm:text-xs text-text-secondary font-medium mb-1.5 sm:mb-2">
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3 text-primary" />
                    {formatDate(post.published_at)}
                  </span>
                  <span>•</span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-3 w-3 text-primary" />
                    {getReadTime(post.content_markdown)}
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-serif text-base sm:text-lg font-bold leading-snug text-accent group-hover:text-primary transition-colors line-clamp-2">
                  <Link href={`/blog/${post.slug}`}>{post.title}</Link>
                </h3>

                {/* Excerpt */}
                <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm leading-relaxed text-text-secondary line-clamp-3">
                  {post.excerpt}
                </p>
              </div>

              {/* Author & Read CTA Footer */}
              <div className="mt-5 pt-3.5 border-t border-orange-100/80 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 sm:h-7 sm:w-7 items-center justify-center rounded-full bg-orange-100 text-[10px] font-bold text-accent">
                    {getAuthorInitials(post.author_name)}
                  </div>
                  <span className="text-xs font-medium text-accent truncate max-w-[90px]">{post.author_name}</span>
                </div>

                <Link
                  href={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary group-hover:translate-x-1 transition-transform"
                >
                  <span>Read guide</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </article>
          ))}
        </section>
      ) : (
        <div className="my-12 rounded-3xl border border-dashed border-orange-200 bg-white p-8 text-center max-w-lg mx-auto">
          <BookOpen className="mx-auto h-10 w-10 text-primary/40 mb-2" />
          <h3 className="text-base font-serif font-bold text-accent">No guides found</h3>
          <p className="mt-1 text-xs sm:text-sm text-text-secondary">
            No articles match your search or selected tag. Try clearing filters.
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedTag('All');
            }}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-3.5 py-1.5 text-xs font-semibold text-accent"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Astroji Trust & Product Banner Section */}
      <section className="mb-12 sm:mb-16 rounded-3xl border border-orange-200/80 bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-amber-500/10 p-6 sm:p-10 text-center relative overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto">
          <ShieldCheck className="mx-auto h-8 w-8 sm:h-10 sm:w-10 text-primary mb-2" />
          <h3 className="font-serif text-xl sm:text-3xl font-bold text-accent">
            100% Lab-Certified Spiritual Products
          </h3>
          <p className="mt-2 text-xs sm:text-base text-text-secondary leading-relaxed">
            Looking for authentic 1–14 Mukhi Rudrakshas or lab-tested Gemstones? Every AstroJi item comes with an official government-recognized lab certificate.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3 sm:gap-4">
            <Link
              href="/products"
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-5 py-2.5 text-xs sm:text-sm font-semibold text-white shadow-md hover:bg-primary transition-all active:scale-95"
            >
              <span>Explore AstroJi Shop</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href="/consultations"
              className="inline-flex items-center gap-2 rounded-xl border border-orange-300 bg-white px-5 py-2.5 text-xs sm:text-sm font-semibold text-accent shadow-xs hover:bg-orange-50 transition-all active:scale-95"
            >
              <span>Book Astro Consultation</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
